export enum CouponBulkUpdateOperation {
  Coverage = "coverage",
  Deactivate = "deactivate",
  Create = "create",
}

export type CouponBulkUpdateRunStatus =
  | "staged"
  | "pending"
  | "running"
  | "completed"
  | "failed"
  | "cancelled";

/**
 * The file check only rejects malformed rows (`invalid`, `duplicate`); every
 * other row is queued as `pending` and the run decides `updated`,
 * `no_change`, `not_found` or `failed`. Create runs also look up brands and
 * existing codes up front, so `no_change` can be set at preview; they end in
 * `created`.
 */
export type CouponBulkUpdateItemStatus =
  | "pending"
  | "updated"
  | "created"
  | "failed"
  | "cancelled"
  | "no_change"
  | "not_found"
  | "invalid"
  | "duplicate";

/** The coupon a create row will write, as resolved at preview. */
export interface ICouponBulkCreateRow {
  domain: string;
  countries: string[];
  code: string;
  summary: string | null;
  discount: number;
  validFrom: string;
  validTo: string;
  coverage: number;
  publishedStatus: number;
  source: string;
  brandId: string | null;
  brandName: string | null;
  /** The cells as uploaded, keyed by column header, for the retry file. */
  raw: Record<string, string>;
}

export interface ICouponBulkCreateColumn {
  header: string;
  required: boolean;
  hint: string;
  example: string;
}

/** The only columns a create file may set; anything else is ignored and listed in the preview. */
export const COUPON_BULK_CREATE_COLUMNS: ICouponBulkCreateColumn[] = [
  { header: "Domain", required: true, hint: "Merchant domain, e.g. kabum.com.br. A pasted URL or www. is trimmed.", example: "kabum.com.br" },
  { header: "Country", required: true, hint: "Kindred country code (UK, not GB). Several: BR;PT", example: "BR" },
  { header: "Code", required: true, hint: "The coupon code as typed at checkout.", example: "SAVE10" },
  { header: "Valid To", required: true, hint: "YYYY-MM-DD (end of that day, UTC) or YYYY-MM-DD HH:mm (UTC).", example: "2026-12-31" },
  { header: "Valid From", required: false, hint: "Same format. Blank means now.", example: "2026-10-16" },
  { header: "Summary", required: false, hint: "What the shopper gets, e.g. 10% off sitewide.", example: "10% off sitewide" },
  { header: "Discount", required: false, hint: "Percentage as a number: 10, 10% or 10% OFF. Blank means 0.", example: "10" },
  { header: "Coverage", required: false, hint: 'None, Sitewide, Category, Product, Manufacturer, or combined with "+". Blank means None.', example: "Sitewide" },
  { header: "Status", required: false, hint: "Draft, Awaiting Approval or Approved. Blank means Draft.", example: "Draft" },
  { header: "Brand ID", required: false, hint: "Only needed when the domain belongs to more than one brand.", example: "" },
  { header: "Source", required: false, hint: "Affiliate network. Blank means manual.", example: "" },
];

export interface ICouponBulkUpdateItem {
  rowNumber: number;
  /** Empty on a create row until the coupon exists. */
  couponId: string;
  /** The value as it appeared in the file, kept for error reporting. */
  rawValue: string | null;
  /** Filled in when the run reaches the row. */
  code: string | null;
  /** Target coverage (coverage runs only). */
  coverage: number | null;
  /** The value the run replaced. */
  previousCoverage: number | null;
  previousPublishedStatus: number | null;
  metadataUpdated: boolean;
  status: CouponBulkUpdateItemStatus;
  message: string | null;
  processedAt: string | null;
  create: ICouponBulkCreateRow | null;
}

export interface ICouponBulkUpdateCounts {
  total: number;
  pending: number;
  updated: number;
  created: number;
  failed: number;
  cancelled: number;
  noChange: number;
  notFound: number;
  invalid: number;
  duplicate: number;
  metadataUpdated: number;
}

export interface ICouponBulkUpdateRunSummary {
  runId: string;
  operation: CouponBulkUpdateOperation;
  status: CouponBulkUpdateRunStatus;
  triggeredBy: string;
  triggeredByName: string | null;
  originalFileName: string;
  /** `<name>_<timestamp>.csv` — the name the file is stored under in blob storage. */
  storedFileName: string | null;
  reason: string | null;
  auditComment: string | null;
  /** Rows sent to the run — the number typed to confirm. */
  queuedCount: number;
  counts: ICouponBulkUpdateCounts;
  stagedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number;
  cancelledBy: string | null;
  errorMessage: string | null;
}

export interface ICouponBulkUpdateStageRequest {
  operation: CouponBulkUpdateOperation;
  fileName: string;
  csvContent: string;
}

export interface ICouponBulkUpdateStageResponse {
  run: ICouponBulkUpdateRunSummary;
  /** Headers in the file that the operation does not read. */
  ignoredColumns: string[];
  /** File-level notes that don't reject rows, e.g. a source checkout won't serve. */
  warnings: string[];
}

export interface ICouponBulkUpdateStartRequest {
  reason: string;
  /** Must equal the number of queued rows — a typed confirmation. */
  confirmCount: number;
}

export interface ICouponBulkUpdateRunsFilter {
  status?: string;
  operation?: string;
  triggeredBy?: string;
  page?: number;
  pageSize?: number;
}

export interface ICouponBulkUpdateRunsResponse {
  runs: ICouponBulkUpdateRunSummary[];
  activeRuns: ICouponBulkUpdateRunSummary[];
  total: number;
  page: number;
  pageSize: number;
  triggeredByOptions: string[];
}

export interface ICouponBulkUpdateItemsResponse {
  items: ICouponBulkUpdateItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ICouponBulkUpdateFileResponse {
  url: string;
  fileName: string;
}
